import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { CalendarDays, Settings, Grid3x3, Clock, BarChart3, Download, Plus, Trash2, Check, X, UserCheck, AlertTriangle } from "lucide-react";

const WD = [["mon", "Lun"], ["tue", "Mar"], ["wed", "Mer"], ["thu", "Gio"], ["fri", "Ven"], ["sat", "Sab"], ["sun", "Dom"]];
const MODES = [["tavolo", "Tavolo"], ["posto", "Posto"], ["appuntamento", "Appuntamento"], ["servizio", "Servizio"], ["corso", "Corso"], ["evento", "Evento"], ["risorsa", "Risorsa"], ["contatto", "Contatto"]];
const STATE_COLORS = {
  richiesta: "text-amber-300", in_attesa: "text-amber-300", confermata: "text-[#00E676]",
  in_corso: "text-cyan-300", completata: "text-zinc-400", annullata_cliente: "text-red-400",
  annullata_attivita: "text-red-400", no_show: "text-red-500", rimborsata: "text-zinc-500",
};
const today = () => new Date().toISOString().slice(0, 10);

function Tab({ id, tab, setTab, icon: Icon, label }) {
  return (
    <button data-testid={`booking-tab-${id}`} onClick={() => setTab(id)}
      className={`text-sm px-3 py-2 rounded-lg border flex items-center gap-2 ${tab === id ? "border-[#00E676] text-[#00E676] bg-[#00E676]/5" : "border-white/10 text-zinc-400"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

export default function BookingPanel() {
  const [tab, setTab] = useState("bookings");
  const [cfg, setCfg] = useState(null);
  const [resources, setResources] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [stats, setStats] = useState(null);
  const [aiTips, setAiTips] = useState(null);
  const [date, setDate] = useState(today());
  const [nr, setNr] = useState({ name: "", kind: "tavolo", capacity: 2, area: "" });

  const loadCfg = () => api.get("/booking/config").then(({ data }) => setCfg(data)).catch(() => {});
  const loadRes = () => api.get("/booking/resources").then(({ data }) => setResources(data)).catch(() => {});
  const loadBk = () => api.get(`/booking/business?date=${date}`).then(({ data }) => setBookings(data)).catch(() => {});
  const loadWl = () => api.get("/booking/waitlist").then(({ data }) => setWaitlist(data)).catch(() => {});
  const loadStats = () => api.get("/booking/stats?period=30d").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => { loadCfg(); loadRes(); loadWl(); loadStats(); }, []);
  useEffect(() => { loadBk(); }, [date]);

  const saveCfg = async () => {
    try { await api.put("/booking/config", cfg); toast.success("Configurazione salvata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addRes = async () => {
    try { await api.post("/booking/resources", nr); setNr({ name: "", kind: "tavolo", capacity: 2, area: "" }); loadRes(); toast.success("Risorsa aggiunta"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delRes = async (id) => { await api.delete(`/booking/resources/${id}`); loadRes(); };
  const act = async (id, action) => {
    try { const { data } = await api.put(`/booking/business/${id}/transition`, { action }); if (data?.penalty > 0) toast.warning(`Penale registrata: €${data.penalty} · Stripe TEST, nessun addebito reale`); loadBk(); loadStats(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportCsv = async () => {
    const res = await api.get("/booking/business.csv", { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "prenotazioni.csv"; a.click();
  };
  const loadTips = () => { setAiTips("loading"); api.get("/booking/ai/occupancy-tips").then(({ data }) => setAiTips(data.tips || [])).catch(() => { setAiTips([]); toast.error("AI non disponibile"); }); };
  const setModes = (m) => { const has = cfg.modes.includes(m); setCfg({ ...cfg, modes: has ? cfg.modes.filter((x) => x !== m) : [...cfg.modes, m] }); };
  const setHours = (wd, field, val) => {
    const oh = { ...(cfg.open_hours || {}) };
    const w = (oh[wd] && oh[wd][0]) || { from: "", to: "" };
    oh[wd] = val === "" && field === "from" ? [] : [{ ...w, [field]: val }];
    setCfg({ ...cfg, open_hours: oh });
  };

  return (
    <div className="space-y-5" data-testid="booking-panel">
      <div>
        <h2 className="text-xl font-display font-bold">Prenotazioni</h2>
        <p className="text-sm text-zinc-500">Motore unico per tavoli, appuntamenti, servizi, corsi, campi e risorse.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Tab id="bookings" tab={tab} setTab={setTab} icon={CalendarDays} label="Agenda" />
        <Tab id="room" tab={tab} setTab={setTab} icon={Grid3x3} label="Sala" />
        <Tab id="config" tab={tab} setTab={setTab} icon={Settings} label="Configurazione" />
        <Tab id="resources" tab={tab} setTab={setTab} icon={Grid3x3} label="Risorse" />
        <Tab id="waitlist" tab={tab} setTab={setTab} icon={Clock} label="Lista d'attesa" />
        <Tab id="stats" tab={tab} setTab={setTab} icon={BarChart3} label="Statistiche" />
      </div>

      {tab === "bookings" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 bg-[#0A0A0C] border-white/10" data-testid="booking-date" />
            <Button size="sm" variant="secondary" onClick={exportCsv} data-testid="booking-export"><Download className="h-4 w-4 mr-1" /> CSV</Button>
          </div>
          {bookings.length === 0 && <div className="text-zinc-500 text-sm py-10 text-center border border-white/10 rounded-xl">Nessuna prenotazione per questa data.</div>}
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-row-${b.id}`} className="rounded-xl border border-white/10 bg-[#1C1C1F] p-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold">{b.time} · {b.party_size}p · {b.customer_name}{b.resource_name ? ` · ${b.resource_name}` : ""}</div>
                <div className="text-xs text-zinc-500">{b.mode}{b.area ? ` · ${b.area}` : ""}{b.notes ? ` · ${b.notes}` : ""}{b.deposit_amount > 0 ? ` · caparra €${b.deposit_amount} (${b.deposit_status})` : ""}</div>
                <div className={`text-xs mt-0.5 ${STATE_COLORS[b.status] || "text-zinc-400"}`}>{b.status}</div>
              </div>
              <div className="flex gap-1">
                {["richiesta", "in_attesa"].includes(b.status) && <Button size="sm" onClick={() => act(b.id, "confirm")} className="bg-[#00E676] text-black" data-testid={`confirm-${b.id}`}><Check className="h-4 w-4" /></Button>}
                {["confermata"].includes(b.status) && <Button size="sm" onClick={() => act(b.id, "checkin")} className="bg-cyan-500 text-black" data-testid={`checkin-${b.id}`}><UserCheck className="h-4 w-4" /></Button>}
                {["in_corso"].includes(b.status) && <Button size="sm" onClick={() => act(b.id, "complete")} className="bg-zinc-600" data-testid={`complete-${b.id}`}>Fine</Button>}
                {["richiesta", "in_attesa", "confermata"].includes(b.status) && <Button size="sm" variant="secondary" onClick={() => act(b.id, "no_show")} data-testid={`noshow-${b.id}`}><AlertTriangle className="h-4 w-4" /></Button>}
                {["richiesta", "in_attesa", "confermata"].includes(b.status) && <Button size="sm" variant="destructive" onClick={() => act(b.id, "cancel")} data-testid={`cancel-${b.id}`}><X className="h-4 w-4" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "room" && (
        <div className="space-y-3" data-testid="booking-room">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 bg-[#0A0A0C] border-white/10" data-testid="room-date" />
          {resources.length === 0 && <div className="text-zinc-500 text-sm py-8 text-center border border-white/10 rounded-xl">Nessuna risorsa configurata. Aggiungi tavoli/campi/stanze dalla scheda Risorse.</div>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {resources.map((r) => {
              const rb = bookings.filter((b) => b.resource_id === r.id && b.status !== "annullata_cliente" && b.status !== "annullata_attivita");
              const busy = rb.length > 0;
              return (
                <div key={r.id} data-testid={`room-res-${r.id}`} className={`rounded-xl border p-4 ${busy ? "border-[#00E676]/40 bg-[#00E676]/5" : "border-white/10 bg-[#1C1C1F]"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{r.name}</span>
                    <span className="text-xs text-zinc-500">{r.kind} · {r.capacity}p{r.area ? ` · ${r.area}` : ""}</span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {rb.length === 0 && <div className="text-xs text-zinc-600">Libero tutta la giornata</div>}
                    {rb.map((b) => (
                      <div key={b.id} className="text-xs flex items-center justify-between">
                        <span>{b.time} · {b.party_size}p · {b.customer_name}</span>
                        <span className={STATE_COLORS[b.status] || "text-zinc-400"}>{b.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "config" && cfg && (
        <div className="space-y-4" data-testid="booking-config">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Modalità attive</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {MODES.map(([k, l]) => (
                <button key={k} data-testid={`mode-${k}`} onClick={() => setModes(k)} className={`px-3 py-1.5 rounded-full text-xs border ${cfg.modes.includes(k) ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]" : "border-white/10 text-zinc-400"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["slot_minutes", "Slot (min)"], ["default_duration", "Durata (min)"], ["buffer_minutes", "Buffer (min)"], ["min_advance_hours", "Anticipo min (h)"], ["max_advance_days", "Max futuro (gg)"], ["cancellation_hours", "Cancellazione (h)"], ["capacity", "Capienza slot"]].map(([k, l]) => (
              <div key={k}><Label className="text-xs text-zinc-400">{l}</Label><Input type="number" value={cfg[k] ?? ""} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value === "" ? null : Number(e.target.value) })} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid={`cfg-${k}`} /></div>
            ))}
            <div><Label className="text-xs text-zinc-400">Caparra fissa (€)</Label><Input type="number" step="0.5" value={cfg.deposit_flat ?? 0} onChange={(e) => setCfg({ ...cfg, deposit_flat: Number(e.target.value) })} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="cfg-deposit-flat" /></div>
            <div><Label className="text-xs text-zinc-400">Caparra/persona (€)</Label><Input type="number" step="0.5" value={cfg.deposit_per_person ?? 0} onChange={(e) => setCfg({ ...cfg, deposit_per_person: Number(e.target.value) })} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="cfg-deposit-pp" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!cfg.auto_confirm} onChange={(e) => setCfg({ ...cfg, auto_confirm: e.target.checked })} className="h-4 w-4 accent-[#00E676]" data-testid="cfg-auto-confirm" /> Conferma automatica (senza approvazione manuale)</label>

          <div className="rounded-xl border border-amber-400/25 bg-amber-400/5 p-4 space-y-3" data-testid="cfg-noshow">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-300"><AlertTriangle className="h-4 w-4" /> Politica no-show / annullamento tardivo</div>
            <p className="text-xs text-zinc-400">Scegli come gestire i clienti che non si presentano o annullano tardi. In modalità Stripe TEST gli importi vengono <span className="text-amber-300">solo registrati</span>, senza addebiti reali.</p>
            <div>
              <Label className="text-xs text-zinc-400">Regola</Label>
              <select value={cfg.noshow_policy || "none"} onChange={(e) => setCfg({ ...cfg, noshow_policy: e.target.value })} className="w-full mt-1 bg-[#0A0A0C] border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="cfg-noshow-policy">
                <option value="none">Nessuna penale (solo stato No-show)</option>
                <option value="percent">Trattieni una % della caparra</option>
                <option value="full">Trattieni l'intera caparra</option>
                <option value="flat">Penale a importo fisso (€)</option>
                <option value="grace">Gratis se annulla in tempo, altrimenti applica penale</option>
              </select>
            </div>
            {cfg.noshow_policy === "percent" && (
              <div><Label className="text-xs text-zinc-400">Percentuale trattenuta (%)</Label><Input type="number" min="0" max="100" value={cfg.noshow_percent ?? 50} onChange={(e) => setCfg({ ...cfg, noshow_percent: Number(e.target.value) })} className="bg-[#0A0A0C] border-white/10 mt-1 w-40" data-testid="cfg-noshow-percent" /></div>
            )}
            {cfg.noshow_policy === "flat" && (
              <div><Label className="text-xs text-zinc-400">Penale fissa (€)</Label><Input type="number" step="0.5" min="0" value={cfg.noshow_flat ?? 0} onChange={(e) => setCfg({ ...cfg, noshow_flat: Number(e.target.value) })} className="bg-[#0A0A0C] border-white/10 mt-1 w-40" data-testid="cfg-noshow-flat" /></div>
            )}
            {cfg.noshow_policy === "grace" && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs text-zinc-400">Annullo gratuito se manca almeno (h)</Label><Input type="number" min="0" value={cfg.noshow_grace_hours ?? 24} onChange={(e) => setCfg({ ...cfg, noshow_grace_hours: Number(e.target.value) })} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="cfg-noshow-grace" /></div>
                <div>
                  <Label className="text-xs text-zinc-400">Penale se annulla in ritardo</Label>
                  <select value={cfg.noshow_fallback || "full"} onChange={(e) => setCfg({ ...cfg, noshow_fallback: e.target.value })} className="w-full mt-1 bg-[#0A0A0C] border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="cfg-noshow-fallback">
                    <option value="percent">% della caparra ({cfg.noshow_percent ?? 50}%)</option>
                    <option value="full">Intera caparra</option>
                    <option value="flat">Importo fisso (€{cfg.noshow_flat ?? 0})</option>
                  </select>
                </div>
                {cfg.noshow_fallback === "percent" && <div><Label className="text-xs text-zinc-400">Percentuale (%)</Label><Input type="number" min="0" max="100" value={cfg.noshow_percent ?? 50} onChange={(e) => setCfg({ ...cfg, noshow_percent: Number(e.target.value) })} className="bg-[#0A0A0C] border-white/10 mt-1 w-40" data-testid="cfg-noshow-grace-percent" /></div>}
                {cfg.noshow_fallback === "flat" && <div><Label className="text-xs text-zinc-400">Importo fisso (€)</Label><Input type="number" step="0.5" min="0" value={cfg.noshow_flat ?? 0} onChange={(e) => setCfg({ ...cfg, noshow_flat: Number(e.target.value) })} className="bg-[#0A0A0C] border-white/10 mt-1 w-40" data-testid="cfg-noshow-grace-flat" /></div>}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Orari di apertura</Label>
            <div className="space-y-1.5 mt-2">
              {WD.map(([k, l]) => {
                const w = (cfg.open_hours?.[k] || [])[0] || { from: "", to: "" };
                return (
                  <div key={k} className="flex items-center gap-2 text-sm">
                    <span className="w-10 text-zinc-400">{l}</span>
                    <Input value={w.from} placeholder="chiuso" onChange={(e) => setHours(k, "from", e.target.value)} className="w-24 bg-[#0A0A0C] border-white/10" data-testid={`hours-${k}-from`} />
                    <span className="text-zinc-600">→</span>
                    <Input value={w.to} placeholder="—" onChange={(e) => setHours(k, "to", e.target.value)} className="w-24 bg-[#0A0A0C] border-white/10" />
                  </div>
                );
              })}
            </div>
          </div>
          <Button onClick={saveCfg} className="bg-[#00E676] text-black" data-testid="cfg-save">Salva configurazione</Button>
        </div>
      )}

      {tab === "resources" && (
        <div className="space-y-3" data-testid="booking-resources">
          <div className="rounded-xl border border-white/10 bg-[#131316] p-4 grid sm:grid-cols-5 gap-2 items-end">
            <div className="sm:col-span-2"><Label className="text-xs text-zinc-400">Nome</Label><Input value={nr.name} onChange={(e) => setNr({ ...nr, name: e.target.value })} placeholder="Tavolo 1 / Campo A / Sala 2" className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="res-name" /></div>
            <div><Label className="text-xs text-zinc-400">Tipo</Label><Input value={nr.kind} onChange={(e) => setNr({ ...nr, kind: e.target.value })} placeholder="tavolo/campo/stanza" className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="res-kind" /></div>
            <div><Label className="text-xs text-zinc-400">Capienza</Label><Input type="number" value={nr.capacity} onChange={(e) => setNr({ ...nr, capacity: Number(e.target.value) })} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="res-cap" /></div>
            <Button onClick={addRes} className="bg-[#00E676] text-black" data-testid="res-add"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
          </div>
          {resources.map((r) => (
            <div key={r.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#1C1C1F] p-3 text-sm">
              <span>{r.name} · <span className="text-zinc-500">{r.kind} · {r.capacity} posti{r.area ? ` · ${r.area}` : ""}</span></span>
              <button onClick={() => delRes(r.id)} className="text-red-400" data-testid={`res-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {resources.length === 0 && <div className="text-zinc-500 text-sm">Nessuna risorsa. Per appuntamenti singoli puoi usare la "Capienza slot" nella configurazione.</div>}
        </div>
      )}

      {tab === "waitlist" && (
        <div className="space-y-2" data-testid="booking-waitlist">
          {waitlist.length === 0 && <div className="text-zinc-500 text-sm py-8 text-center">Nessun cliente in lista d'attesa.</div>}
          {waitlist.map((w) => (
            <div key={w.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#1C1C1F] p-3 text-sm">
              <span>{w.customer_name} · {w.date} · {w.party_size}p {w.time_pref ? `· ${w.time_pref}` : ""}</span>
              <span className={w.status === "notified" ? "text-[#00E676]" : "text-amber-300"}>{w.status}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "stats" && stats && (
        <div className="space-y-4" data-testid="booking-stats">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Prenotazioni", stats.total], ["Conferma %", stats.confirm_rate], ["Annullamenti %", stats.cancel_rate], ["No-show %", stats.no_show_rate], ["Caparre €", stats.deposits_collected], ["Penali sim. €", stats.penalties_simulated ?? 0], ["Ora migliore", stats.best_hour || "—"], ["Giorno migliore", stats.best_weekday || "—"], ["Clienti ricorrenti", stats.recurring_customers]].map(([l, v], i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-[#1C1C1F] p-4"><div className="text-2xl font-bold text-[#00E676]">{v}</div><div className="text-xs text-zinc-500 mt-1">{l}</div></div>
            ))}
          </div>
          <div className="rounded-xl border border-[#7DF9FF]/25 bg-[#7DF9FF]/5 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-[#7DF9FF]">✨ AI — Come aumentare l'occupazione</span>
              <Button size="sm" variant="secondary" onClick={loadTips} data-testid="ai-tips-btn">{aiTips === "loading" ? "…" : "Genera consigli"}</Button>
            </div>
            {Array.isArray(aiTips) && (
              <ul className="mt-3 space-y-1.5 text-sm text-zinc-300 list-disc pl-5" data-testid="ai-tips-list">
                {aiTips.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
